import { useState, ChangeEvent, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { IImportFileStatus } from 'ImportPackage/MainModule/interfaces/IImportFileStatus';
import { SearchInput } from 'UIPackage/MainModule/components/SearchInput';
import { Button } from 'UIPackage/MainModule/components/Button';
import { FilterCheckbox } from 'UIPackage/MainModule/components/FilterCheckbox';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { UploadFileTypesEnum } from 'CorePackage/MainModule/enums/UploadFileTypesEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { UploadModal, IUploadModalForm } from './UploadModal';
import { ImportFiltersModal, IFiltersModalForm } from './ImportFiltersModal';
import styles from './ImportPanel.module.scss';
import { getImportCurrentUser, getImportSearchValue } from '../selectors/importSelectors';
import {
  importCreateTemplateWithFile,
  importGetData,
  importSetCurrentUser,
  importSetSearchValue,
} from '../actions/importActions';
import { IImportFilters } from '../interfaces/IImportFilters';

interface IImportPanelProps {
  dataCount: number;
  fileTypes: (keyof typeof UploadFileTypesEnum)[];
  statuses: IImportFileStatus[];
  isDeleteButtonEnabled?: boolean;
  onFiltersApply: (filters: IImportFilters) => void;
  onDelete: () => void;
}

interface IImportPanelStoreProps {
  searchValue: string;
  isCurrentUserChecked: boolean;
}

const ImportPanelWithoutStore = ({
  dataCount,
  fileTypes,
  statuses,
  isDeleteButtonEnabled,
  isCurrentUserChecked,
  searchValue,
  onFiltersApply,
  onDelete,
}: IImportPanelProps & IImportPanelStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [isFiltersModalVisible, setIsFiltersModalVisible] = useState(false);

  const handleUploadButtonClick = useCallback(() => setIsUploadModalVisible(true), []);

  const handleUploadModalApply = useCallback(
    (values: IUploadModalForm) => {
      dispatch(importCreateTemplateWithFile(values, values.planId || undefined));
    },
    [dispatch],
  );

  const closeUploadModal = useCallback(() => setIsUploadModalVisible(false), []);

  const handleSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      dispatch(importSetSearchValue(String(value)));
    },
    [dispatch],
  );

  const handleSearchSubmit = useCallback(() => {
    dispatch(importGetData());
  }, [dispatch]);

  const handleFilterCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(importSetCurrentUser(e.target.checked));
      dispatch(importGetData());
    },
    [dispatch],
  );

  const handleFilterButtonClick = useCallback(() => setIsFiltersModalVisible(true), []);

  const handleFiltersModalApply = useCallback(
    (values: IFiltersModalForm) => {
      onFiltersApply({
        fileTypes: values?.fileType?.map(item => item.value) || [],
        statuses: values?.status?.map(item => item.value) || [],
      });
    },
    [onFiltersApply],
  );

  const closeFiltersModal = useCallback(() => setIsFiltersModalVisible(false), []);

  return (
    <div className="shared-panel-container">
      <div className={styles.Actions}>
        <Button
          startIcon={styles.AddIcon}
          text="Загрузить"
          color="primary"
          id="addButton"
          onClick={handleUploadButtonClick}
        />
        <Button
          className={styles.DeleteButton}
          startIcon={styles.DeleteIcon}
          text="Удалить"
          onClick={onDelete}
          disabled={!isDeleteButtonEnabled}
        />
        <SearchInput
          className={styles.SearchInput}
          value={searchValue}
          name="searchString"
          placeholder="Искать"
          onChange={handleSearchInputChange}
          onSubmit={handleSearchSubmit}
        />
        <Tooltip
          placement="right"
          content={
            <div className={styles.TooltipContent}>
              Доступен поиск загрузок по следующим критериям:
              <ul>
                <li>Номер загрузки;</li>
                <li>Номер плана;</li>
                <li>Номер КНМ;</li>
                <li>Наименование органа контроля.</li>
              </ul>
              Расширенный поиск загрузок осуществляется с помощью Фильтра.
            </div>
          }
        >
          <span className={styles.TooltipIcon} />
        </Tooltip>
      </div>
      <div className={styles.Filters}>
        <p className={styles.CurrentFilter}>
          Планы моей прокуратуры&nbsp;
          <small className={styles.CurrentFilterNumbers}>({dataCount})</small>
        </p>
        <span className={styles.FilterCheckboxLabel}>Мои загрузки</span>
        <FilterCheckbox
          className={styles.FilterCheckbox}
          id="importFilterToggler"
          name="importFilterToggler"
          checked={isCurrentUserChecked}
          onChange={handleFilterCheckboxChange}
          whiteBackground
        />
        <Button className={styles.FilterButton} text="Фильтры" color="primary" onClick={handleFilterButtonClick} />
      </div>
      <UploadModal
        visible={isUploadModalVisible}
        fileTypes={fileTypes}
        onApply={handleUploadModalApply}
        onClose={closeUploadModal}
      />
      <ImportFiltersModal
        visible={isFiltersModalVisible}
        fileTypes={fileTypes}
        statuses={statuses}
        onApply={handleFiltersModalApply}
        onClose={closeFiltersModal}
      />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IImportPanelStoreProps => ({
  searchValue: getImportSearchValue(state),
  isCurrentUserChecked: getImportCurrentUser(state),
});

export const ImportPanel = connect(mapStateToProps)(ImportPanelWithoutStore);
